import * as React from "react"
import { useEditorState } from "@tiptap/react"
import { Button } from "../../tiptap-ui-primitive/button"
import { LinkIcon } from "../../tiptap-icons/link-icon"
import { useTiptapEditor } from "../../hooks/use-tiptap-editor"
import "./link-popover.scss"

export const LinkPopover: React.FC = () => {
  const editor = useTiptapEditor()
  const [isOpen, setIsOpen] = React.useState(false)
  const [url, setUrl] = React.useState("")
  const inputRef = React.useRef<HTMLInputElement>(null)
  // Сохраняем позицию селекции при открытии
  const savedSelectionRef = React.useRef<{ from: number; to: number } | null>(null)

  const state = useEditorState({
    editor,
    selector: (ctx) => ({
      isActive: ctx.editor?.isActive("link") ?? false,
      href: ctx.editor?.getAttributes("link").href ?? "",
    }),
  })

  React.useEffect(() => {
    if (isOpen && state.href) {
      setUrl(state.href)
    }
  }, [isOpen, state.href])

  const handleOpen = () => {
    // Сохраняем текущую селекцию
    if (editor) {
      const { from, to } = editor.state.selection
      savedSelectionRef.current = { from, to }
    }
    setIsOpen(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleClose = () => {
    setIsOpen(false)
    setUrl("")
    savedSelectionRef.current = null
  }

  const handleSetLink = () => {
    if (!editor) return
    
    // Восстанавливаем селекцию если она была сохранена
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current
      editor.chain().setTextSelection({ from, to }).run()
    }
    
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    } else {
      editor.chain().focus().unsetLink().run()
    }
    handleClose()
  }

  const handleRemoveLink = () => {
    if (!editor) return
    
    // Восстанавливаем селекцию
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current
      editor.chain().setTextSelection({ from, to }).run()
    }
    
    editor.chain().focus().unsetLink().run()
    handleClose()
  }

  return (
    <div className="tiptap-link-popover-wrapper">
      <Button
        onClick={handleOpen}
        data-active={state.isActive}
        aria-label="Добавить ссылку"
        title="Добавить ссылку"
      >
        <LinkIcon className="tiptap-button-icon" />
      </Button>
      {isOpen && (
        <div className="tiptap-link-popover">
          <input
            ref={inputRef}
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Введите URL..."
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSetLink()
              if (e.key === "Escape") handleClose()
            }}
          />
          <div className="tiptap-link-popover-actions">
            <Button onClick={handleSetLink}>Применить</Button>
            {state.isActive && (
              <Button onClick={handleRemoveLink} data-style="ghost">Удалить</Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
